import type {
	AlgoliaProductCard,
	ProductCard as ProductCardType,
	ProductDetails,
} from "@raumagarn/formatters";
import { Skeleton } from "~/components/ui/Skeleton";
import { PC } from "~/features/products/ProductCard/ProductCardTemplate";
import { GallerySkeleton } from "~/features/products/ProductCard/parts/Gallery/Gallery";
import { ProductCardTags } from "~/features/products/ProductCard/parts/ProductCardTags/ProductCardTags";
import {
	Colors,
	WeightAndLength,
	YarnWidget,
} from "~/features/products/ProductCard/parts/YarnWidget/YarnWidget";

const { Card, Gallery, Content, Description, Price, Name, Inline } = PC;

export interface ProductCardProps {
	productCard:
		| Omit<ProductCardType, "relatedProducts" | "variants">
		| Omit<AlgoliaProductCard, "relatedProducts" | "variants">
		| Omit<ProductDetails, "relatedProducts" | "variants">;
	onClick?: () => void;
}

export const ProductCard = ({ productCard, onClick }: ProductCardProps) => {
	if (!productCard.productType) return null;

	const {
		name,
		uri,
		gallery,
		sku,
		fits,
		yarnType,
		yarnLengthAndWeight,
		yarnOptionLength,
		yarnMaterials,
		cannonicalCategory,
		media,
	} = productCard;

	const image = gallery?.image || media?.[0];
	const secondImage = gallery?.secondImage || media?.[1];

	return (
		<Card name={name ?? ""} slug={uri || "#"} onClick={onClick}>
			<Gallery url={image?.href ?? ""} alt={image?.alt ?? ""} secondImage={secondImage}>
				<ProductCardTags productCard={productCard} />
			</Gallery>
			<Content>
				{productCard.productType === "accessory" && (
					<>
						<Description>
							<Name>{name ?? ""}</Name>
						</Description>

						<Price pricelist={productCard.pricelist} />
					</>
				)}
				{productCard.productType === "recipe" && (
					<>
						<Description>
							<Inline>
								{sku && <strong>{sku}</strong>}
								{fits && <span>{fits}</span>}
							</Inline>

							{name && <Name>{name}</Name>}

							<Inline>
								{yarnType && yarnType.length > 0 ? <strong>{yarnType.at(0)}</strong> : null}
							</Inline>
						</Description>

						<Inline>
							<Price pricelist={productCard.pricelist} />
						</Inline>
					</>
				)}
				{productCard.productType === "yarn" && (
					<>
						<Description>
							{name && <Name>{name}</Name>}

							<YarnWidget>
								{yarnLengthAndWeight && <WeightAndLength>{yarnLengthAndWeight}</WeightAndLength>}
								<Colors
									total={yarnOptionLength ?? 0}
									swatchUrl={secondImage?.href ?? image?.href}
								/>
							</YarnWidget>

							<span>{yarnMaterials}</span>
						</Description>
						<Price pricelist={productCard.pricelist} />
					</>
				)}
				{productCard.productType === "booklet" && (
					<>
						<Description>
							<Inline>
								<strong>{sku}</strong>
								<span>{cannonicalCategory}</span>
							</Inline>
							<Name>{name}</Name>
							<strong>
								{yarnType && yarnType.length > 0 ? <strong>{yarnType.at(0)}</strong> : null}
							</strong>
						</Description>
						<Price pricelist={productCard.pricelist} />
					</>
				)}
			</Content>
		</Card>
	);
};

export const ProductCardSkeleton = () => {
	return (
		<Card name={""} slug={"#"}>
			<GallerySkeleton />
			<Content>
				<Description>
					<Skeleton height="1.5rem" />

					<YarnWidget>
						<Skeleton height="1.25rem" />
						<Skeleton height="1.25rem" />
					</YarnWidget>

					<Skeleton height="1rem" />
				</Description>
			</Content>
		</Card>
	);
};
